import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { authAPI } from '../api/authApi';
import { UiSettingsFields } from './UiSettingsFields';
import { UserTypeField } from './UserTypeField';
import { uiSettingsFromUser, type UiSettingsFormValue } from './uiSettings';
import { readUserType, type UserType } from './userType';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog';

const ProfilePage: React.FC = () => {
    const { user, loading, refreshUser, signOut } = useAuth();
    const navigate = useNavigate();
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [organization, setOrganization] = useState('');
    const [saveBusy, setSaveBusy] = useState(false);
    const [saveError, setSaveError] = useState<string | null>(null);
    const [saveSuccess, setSaveSuccess] = useState(false);
    const [uiSettings, setUiSettings] = useState<UiSettingsFormValue>({
        hideInbox: false,
        hideActions: false,
        hideSubmissions: false,
    });
    const [userType, setUserType] = useState<UserType>('submitter');
    const [uiSaveBusy, setUiSaveBusy] = useState(false);
    const [uiSaveError, setUiSaveError] = useState<string | null>(null);
    const [uiSaveSuccess, setUiSaveSuccess] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [deleteConfirmText, setDeleteConfirmText] = useState('');
    const [deleteBusy, setDeleteBusy] = useState(false);
    const [deleteError, setDeleteError] = useState<string | null>(null);

    const deleteConfirmMatches = deleteConfirmText.trim().toLowerCase() === 'i am sure';

    const email = typeof user?.email === 'string' ? user.email : '';

    useEffect(() => {
        if (!user) return;
        setFirstName((user.firstName ?? user.info?.firstName ?? '').trim());
        setLastName((user.lastName ?? user.info?.lastName ?? '').trim());
        setOrganization((user.organization ?? user.info?.organization ?? '').trim());
        setUiSettings(uiSettingsFromUser(user));
        setUserType(readUserType(user));
    }, [user]);

    const handleSave = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!user || saveBusy) return;
        const f = firstName.trim();
        const l = lastName.trim();
        if (!f || !l) {
            setSaveError('First name and last name are required');
            return;
        }
        setSaveBusy(true);
        setSaveError(null);
        setSaveSuccess(false);
        try {
            await authAPI.updateUser({ firstName: f, lastName: l, organization: organization.trim() });
            await refreshUser();
            setSaveSuccess(true);
        } catch (err: unknown) {
            const message = err instanceof Error ? err.message : 'Failed to update profile';
            setSaveError(message);
        } finally {
            setSaveBusy(false);
        }
    };

    const handleSaveUiSettings = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!user || uiSaveBusy) return;
        setUiSaveBusy(true);
        setUiSaveError(null);
        setUiSaveSuccess(false);
        try {
            await authAPI.updateUser({ uiSettings, userType });
            await refreshUser();
            setUiSaveSuccess(true);
        } catch (err: unknown) {
            const message = err instanceof Error ? err.message : 'Failed to update interface settings';
            setUiSaveError(message);
        } finally {
            setUiSaveBusy(false);
        }
    };

    const handleDeleteConfirm = async () => {
        if (deleteBusy) return;
        setDeleteBusy(true);
        setDeleteError(null);
        try {
            await authAPI.deleteAccount();
            await signOut();
            navigate('/login');
            return;
        } catch (err: unknown) {
            const message = err instanceof Error ? err.message : 'Failed to delete account';
            setDeleteError(message);
            const is401 = message.includes('401') || message.includes('Authentication');
            if (is401) {
                await signOut();
                navigate('/login');
            }
        } finally {
            setDeleteBusy(false);
        }
    };

    if (loading) {
        return (
            <div className="p-6 flex items-center justify-center min-h-[200px]">
                <p className="text-gray-500 dark:text-gray-400">Loading...</p>
            </div>
        );
    }

    if (!user) {
        navigate('/login');
        return null;
    }

    return (
        <div className="p-6 max-w-2xl mx-auto space-y-6">
            <Card>
                <CardHeader>
                    <CardTitle>Profile</CardTitle>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                        Update your details. Email cannot be changed here.
                    </p>
                </CardHeader>
                <CardContent>
                    <form onSubmit={handleSave} className="space-y-4">
                        {saveError && (
                            <Alert variant="destructive">
                                <AlertDescription>{saveError}</AlertDescription>
                            </Alert>
                        )}
                        {saveSuccess && (
                            <Alert>
                                <AlertDescription>Profile updated successfully.</AlertDescription>
                            </Alert>
                        )}
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label htmlFor="profileFirstName">First name</Label>
                                <Input
                                    id="profileFirstName"
                                    value={firstName}
                                    onChange={(e) => setFirstName(e.target.value)}
                                    disabled={saveBusy}
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="profileLastName">Last name</Label>
                                <Input
                                    id="profileLastName"
                                    value={lastName}
                                    onChange={(e) => setLastName(e.target.value)}
                                    disabled={saveBusy}
                                />
                            </div>
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="profileEmail">Email</Label>
                            <Input
                                id="profileEmail"
                                type="email"
                                value={email}
                                disabled
                                className="bg-gray-50 dark:bg-gray-800"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="profileOrganization">Organization</Label>
                            <Input
                                id="profileOrganization"
                                value={organization}
                                onChange={(e) => setOrganization(e.target.value)}
                                disabled={saveBusy}
                            />
                        </div>
                        <Button type="submit" disabled={saveBusy}>
                            {saveBusy ? 'Saving...' : 'Save changes'}
                        </Button>
                    </form>
                </CardContent>
            </Card>

            <Card>
                <CardHeader>
                    <CardTitle>Interface</CardTitle>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                        Choose the dashboard for your account, and hide parts of the app. Your data stays in place. Direct links to a hidden section return to Home.
                    </p>
                </CardHeader>
                <CardContent>
                    <form onSubmit={handleSaveUiSettings} className="space-y-4">
                        {uiSaveError && (
                            <Alert variant="destructive">
                                <AlertDescription>{uiSaveError}</AlertDescription>
                            </Alert>
                        )}
                        {uiSaveSuccess && (
                            <Alert>
                                <AlertDescription>Interface settings saved.</AlertDescription>
                            </Alert>
                        )}
                        <UserTypeField
                            id="profile-user-type"
                            value={userType}
                            onChange={setUserType}
                            disabled={uiSaveBusy}
                        />
                        <UiSettingsFields
                            idPrefix="profile-ui"
                            value={uiSettings}
                            onChange={setUiSettings}
                            disabled={uiSaveBusy}
                        />
                        <Button type="submit" disabled={uiSaveBusy}>
                            {uiSaveBusy ? 'Saving...' : 'Save interface'}
                        </Button>
                    </form>
                </CardContent>
            </Card>

            <Card className="border-red-200 dark:border-red-900">
                <CardHeader>
                    <CardTitle className="text-red-700 dark:text-red-400">Remove account</CardTitle>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                        Permanently delete your account and all associated data. This cannot be undone.
                    </p>
                </CardHeader>
                <CardContent>
                    <Button
                        variant="destructive"
                        onClick={() => {
                            setDeleteError(null);
                            setDeleteConfirmText('');
                            setDeleteDialogOpen(true);
                        }}
                    >
                        Remove account
                    </Button>
                </CardContent>
            </Card>

            <AlertDialog open={deleteDialogOpen} onOpenChange={(open) => {
                if (!open) {
                    setDeleteError(null);
                    setDeleteConfirmText('');
                }
                setDeleteDialogOpen(open);
            }}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Remove account</AlertDialogTitle>
                        <AlertDialogDescription>
                            This will permanently delete your account and cannot be undone. You will need to register
                            again to use the app.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <div className="space-y-2 py-2">
                        <Label htmlFor="deleteConfirm">Type &quot;i am sure&quot; to confirm</Label>
                        <Input
                            id="deleteConfirm"
                            value={deleteConfirmText}
                            onChange={(e) => setDeleteConfirmText(e.target.value)}
                            placeholder="i am sure"
                            disabled={deleteBusy}
                            className="mt-2"
                        />
                    </div>
                    {deleteError && (
                        <Alert variant="destructive">
                            <AlertDescription>{deleteError}</AlertDescription>
                        </Alert>
                    )}
                    <AlertDialogFooter>
                        <AlertDialogCancel disabled={deleteBusy}>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={(e) => {
                                e.preventDefault();
                                handleDeleteConfirm();
                            }}
                            disabled={deleteBusy || !deleteConfirmMatches}
                            className="bg-red-600 hover:bg-red-700"
                        >
                            {deleteBusy ? 'Removing...' : 'Remove account'}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
};

export default ProfilePage;
